import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack } from '@mui/material';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TimerIcon from '@mui/icons-material/Timer';
import { useTheme } from '@mui/material/styles';

export interface FocusModeProps {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  wordCount?: number;
}

export function FocusMode({
  children,
  open,
  onClose,
  wordCount,
}: FocusModeProps) {
  const theme = useTheme();
  const [elapsedMinutes, setElapsedMinutes] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  // Timer logic
  useEffect(() => {
    if (open) {
      startTimeRef.current = Date.now();
      setElapsedMinutes(0);
      timerRef.current = setInterval(() => {
        const mins = Math.floor((Date.now() - startTimeRef.current) / 60_000);
        setElapsedMinutes(mins);
      }, 10_000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [open, onClose]);

  if (!open) return null;

  const bgColor = theme.palette.mode === 'dark' ? '#121212' : '#ffffff';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        backgroundColor: bgColor,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {/* Floating toolbar */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: 'fixed',
          top: 16,
          right: 24,
          zIndex: 1301,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.9)'
              : 'rgba(245, 245, 245, 0.9)',
          borderRadius: 2,
          px: 2,
          py: 1,
          backdropFilter: 'blur(8px)',
          boxShadow: theme.shadows[4],
        }}
      >
        {wordCount !== undefined && (
          <Chip
            size="small"
            label={`${wordCount.toLocaleString()} words`}
            variant="outlined"
          />
        )}
        <Chip
          size="small"
          icon={<TimerIcon sx={{ fontSize: 16 }} />}
          label={`${elapsedMinutes}m`}
          variant="outlined"
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<FullscreenExitIcon />}
          onClick={onClose}
        >
          Exit Focus
        </Button>
      </Stack>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 900,
          mx: 'auto',
          width: '100%',
          pt: 8,
          pb: 4,
          px: 4,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
